import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, Observable, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  HandoverReferenceNumsResponse,
  HandoverResponse,
} from 'src/app/core/models/reports/exceptions-reports.model';
import {
  BuildingScanExportReport,
  BuildingScanExportReportRequest,
  BuildingScanExportRoutes,
  BuildingScanExportRoutesRequest,
  ExcepProcReport,
  ExcepProcReportRequest,
  ExcepProcRoutes,
  ExcepProcRoutesRoutesRequest,
} from 'src/app/core/models/reports/reports.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class ExceptionsReportsService {
  baseUrl = environment.apiUrl.concat('report-management/');

  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  /**
   * Get Reference Numbers for Handover Scan Report
   *
   * @param {string} locationCode
   * @param {string} groupCode
   * @return {*}  {Observable<RetailActivityReport>}
   * @memberof ExceptionsReportsService
   */
  getHandoverReferenceNumbers(
    locationCode: string,
    date: string,
  ): Observable<HandoverReferenceNumsResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<HandoverReferenceNumsResponse>(
        this.baseUrl.concat('handover-scan-report/reference-numbers'),
        {
          params: { locationCode: locationCode, date: date },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<HandoverReferenceNumsResponse>(
            error,
            error.status,
            'Exceptions Report Service: Get Handover Reference Numbers Service',
            BANNER_TYPE.REPORTS_REFERENCE_NUMBER_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get Handover Scan Reports
   *
   * @param {string} locationCode
   * @param {string} groupCode
   * @return {*}  {Observable<RetailActivityReport>}
   * @memberof ExceptionsReportsService
   */
  getHandoverReports(
    locationCode: string,
    date: string,
    referenceNum: string,
  ): Observable<HandoverResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<HandoverResponse>(this.baseUrl.concat('handover-scan-report'), {
        params: {
          locationCode: locationCode,
          date: date,
          referenceNumber: referenceNum,
        },
      })
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<HandoverResponse>(
            error,
            error.status,
            'Exceptions Report Service: Get Handover Reports Service',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get BuildingScanExport Routes for given location
   *
   * @param {BuildingScanExportRoutesRequest} request
   * @return {*}  {Observable<BuildingScanExportRoutes>}
   * @memberof ExceptionsReportsService
   */
  getBuildingScanExportRoutes(
    request: BuildingScanExportRoutesRequest,
  ): Observable<BuildingScanExportRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<BuildingScanExportRoutes>(
        this.baseUrl.concat('building-scan-export-report/routes'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<BuildingScanExportRoutes>(
            error,
            error.status,
            'Exceptions Report Service: Get BuildingScanExport Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
  /**
   *Get a specific BuildingScanExport report based on the passed in location code
   *
   * @param {BuildingScanExportReportRequest} request
   * @return {*}  {Observable<BuildingScanExport>}
   * @memberof ExceptionsReportsService
   */
  getBuildingScanExportReport(
    request: BuildingScanExportReportRequest,
  ): Observable<BuildingScanExportReport> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<BuildingScanExportReport>(
        this.baseUrl.concat('building-scan-export-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<BuildingScanExportReport>(
            error,
            error.status,
            'Exceptions Report Service: Get BuildingScanExport report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get ExceptionProcessing Routes for given location
   *
   * @param {ExcepProcRoutesRoutesRequest} request
   * @return {*}  {Observable<ExcepProcRoutes>}
   * @memberof ExceptionsReportsService
   */
  getExceptionProcessingRoutes(
    request: ExcepProcRoutesRoutesRequest,
  ): Observable<ExcepProcRoutes> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<ExcepProcRoutes>(
        this.baseUrl.concat('exception-processing-report/routes'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ExcepProcRoutes>(
            error,
            error.status,
            'Exceptions Report Service: Get ExceptionProcessing Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   *Get a specific ExceptionProcessing report based on the passed in location code
   *
   * @param {ExcepProcReportRequest} request
   * @return {*}  {Observable<BuildingScanExport>}
   * @memberof ExceptionsReportsService
   */
  getExceptionProcessingReport(
    request: ExcepProcReportRequest,
  ): Observable<ExcepProcReport> {
    this.commonUtils.updateLoadingModal(true);

    return this.http
      .get<ExcepProcReport>(
        this.baseUrl.concat('exception-processing-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<ExcepProcReport>(
            error,
            error.status,
            'Exceptions Report Service: Get ExceptionProcessing report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
